import { Button } from "@/components/ui/button";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
} from "@/components/ui/pagination";
import { ChevronLeft, ChevronRight } from "lucide-react";

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface TablePaginatorProps {
  pagination: PaginationInfo;
  onPageChange: (page: number) => void;
  className?: string;
}

export const TablePaginator = ({
  pagination,
  onPageChange,
  className = "",
}: TablePaginatorProps) => {
  const { page, totalPages, total, limit, hasNext, hasPrev } = pagination;

  if (totalPages <= 1) {
    return null;
  }

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      // Mostrar todas as páginas se houver poucas
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Sempre mostrar primeira página
      pages.push(1);

      // Calcular páginas ao redor da atual
      let start = Math.max(2, page - 1);
      let end = Math.min(totalPages - 1, page + 1);

      // Ajustar se estivermos no início
      if (page <= 2) {
        end = Math.min(totalPages - 1, 3);
      }

      // Ajustar se estivermos no final
      if (page >= totalPages - 1) {
        start = Math.max(2, totalPages - 2);
      }

      // Adicionar ellipsis antes se necessário
      if (start > 2) {
        pages.push("ellipsis-start");
      }

      // Adicionar páginas ao redor da atual
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      // Adicionar ellipsis depois se necessário
      if (end < totalPages - 1) {
        pages.push("ellipsis-end");
      }

      // Sempre mostrar última página
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  return (
    <div className={`flex items-center justify-between px-2 py-4 gap-4 ${className}`}>
      <div className="text-sm text-muted-foreground whitespace-nowrap">
        {startItem} a {endItem} de {total} registro{total !== 1 ? "s" : ""}
      </div>

      <Pagination className="mr-auto">
        <PaginationContent>
          <PaginationItem>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page - 1)}
              disabled={!hasPrev}
              className="gap-1"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </PaginationItem>

          {getPageNumbers().map((pageNum, index) => {
            if (pageNum === "ellipsis-start" || pageNum === "ellipsis-end") {
              return (
                <PaginationItem key={`ellipsis-${index}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              );
            }

            const pageNumber = pageNum as number;
            return (
              <PaginationItem key={pageNumber}>
                <Button
                  variant={pageNumber === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => onPageChange(pageNumber)}
                  className="min-w-[40px]"
                >
                  {pageNumber}
                </Button>
              </PaginationItem>
            );
          })}

          <PaginationItem>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page + 1)}
              disabled={!hasNext}
              className="gap-1"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
};

